import type { CanonicalGame } from "@hanjan-shieo/content";

export type ArcadeRuntimeMode = "authentic_interactive" | "reference_only";

export const verifiedArcadeGameIds = [
  "same-word-sync",
  "three-six-nine",
  "baskin-31",
  "nunchi-count",
  "bouncing-ball-chant",
  "year-quiz",
  "three-syllable-chain",
  "word-association",
  "categories",
  "mafia-local",
  "open-dice-rules",
] as const;

const authenticInteractiveIds = new Set<string>(verifiedArcadeGameIds);

export function arcadeRuntimeMode(game: Pick<CanonicalGame, "canonicalId" | "playability">): ArcadeRuntimeMode {
  if (game.playability === "reference_only" || game.playability === "safety_blocked") return "reference_only";
  return authenticInteractiveIds.has(game.canonicalId) ? "authentic_interactive" : "reference_only";
}

export function isVerifiedArcadeGame(game: Pick<CanonicalGame, "canonicalId" | "playability">) {
  return arcadeRuntimeMode(game) === "authentic_interactive";
}

export function isClapNumber(value: number) {
  return String(value).split("").filter((digit) => ["3", "6", "9"].includes(digit)).length;
}

export function baskinNumbers(current: number, take: number) {
  if (!Number.isInteger(current) || current < 0 || current >= 31) throw new Error("invalid_current");
  if (!Number.isInteger(take) || take < 1 || take > 3) throw new Error("invalid_take");
  return Array.from({ length: Math.min(take, 31 - current) }, (_, index) => current + index + 1);
}

function firstSyllable(value: string) {
  return [...value.trim()][0] ?? "";
}

function lastSyllable(value: string) {
  return [...value.trim()].at(-1) ?? "";
}

export type WordChainValidation = { ok: true; word: string } | { ok: false; reason: "empty" | "length" | "duplicate" | "link" };

export function validateWordChain(input: {
  candidate: string;
  previous?: string | null;
  used: string[];
  exactLength?: number;
}): WordChainValidation {
  const word = input.candidate.trim().replace(/\s+/g, "");
  if (!word) return { ok: false, reason: "empty" };
  if (input.exactLength && [...word].length !== input.exactLength) return { ok: false, reason: "length" };
  if (input.used.includes(word)) return { ok: false, reason: "duplicate" };
  if (input.previous && lastSyllable(input.previous) !== firstSyllable(word)) return { ok: false, reason: "link" };
  return { ok: true, word };
}

export type SameMindChoice = 0 | 1 | 2 | "pass";
export type SameMindState = {
  playerCount: number;
  currentPlayer: number;
  sealedChoices: SameMindChoice[];
  phase: "choosing" | "reveal";
};

export function createSameMindState(playerCount: number): SameMindState {
  if (!Number.isInteger(playerCount) || playerCount < 3 || playerCount > 8) throw new Error("invalid_player_count");
  return { playerCount, currentPlayer: 1, sealedChoices: [], phase: "choosing" };
}

export function submitSameMindChoice(state: SameMindState, choice: SameMindChoice): SameMindState {
  if (state.phase !== "choosing") throw new Error("round_already_complete");
  if (![0, 1, 2, "pass"].includes(choice)) throw new Error("invalid_choice");
  const sealedChoices = [...state.sealedChoices, choice];
  if (sealedChoices.length === state.playerCount) return { ...state, sealedChoices, phase: "reveal" };
  return { ...state, sealedChoices, currentPlayer: state.currentPlayer + 1 };
}

export function revealSameMindChoices(state: SameMindState): readonly [number, number, number] | null {
  if (state.phase !== "reveal" || state.sealedChoices.length !== state.playerCount) return null;
  const counts: [number, number, number] = [0, 0, 0];
  for (const choice of state.sealedChoices) if (choice !== "pass") counts[choice] += 1;
  return counts;
}
